import { RootState } from 'src/store';
import { alertSnackbar } from 'src/store/ui/actions';
import { ensureApiError } from 'src/utils/Errors';

import { ApiMethods, ApiTags, appAPI } from '.';

// Maps optimistic temp IDs to server-assigned real IDs.
// Resolves a race condition where the note modal holds a stale temp ID snapshot
// when the user edits/deletes a note immediately after creating it.
const tempToRealNoteId = new Map<string, string>();
const resolveNoteId = (id: string): string => tempToRealNoteId.get(id) ?? id;

type NoteEntityType = 'client' | 'company';

export const NOTES_LIST_LIMIT = 50;

export interface Note {
  createdAt: string;
  creatorId: string;
  /* Contains the plaintext of the note's title */
  title: string;
  /* Contains the HTML representing the note's body */
  content: string;
  id: string;
  entityId: string;
  entityType: NoteEntityType;
  updatedDate: string;
}

interface CreateNoteParams {
  title: string;
  content: string;
  entityType: NoteEntityType;
  entityId: string;
}

interface UpdateNoteParams {
  id: string;
  content: string;
  title: string;
  entityType: NoteEntityType;
  entityId: string;
}

interface ListNotesParams {
  entityType: NoteEntityType;
  entityId: string;
  limit?: number;
  nextToken?: string;
}

interface ListNotesResponse {
  data: Note[];
  nextToken?: string;
}

export const notesApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    createNote: build.mutation<Note, CreateNoteParams>({
      query: (body) => ({
        path: '/v1/notes',
        method: ApiMethods.post,
        options: { body },
      }),
      async onQueryStarted(payload, { dispatch, queryFulfilled, getState }) {
        const state = getState() as RootState;
        const currentUser = state.users.internalUsers.find(
          (u) => u.cognitoUsername === state.user.id,
        );
        const tempId = `temp-${Math.random().toString(36).slice(2)}`;
        const now = new Date().toISOString();

        const optimisticNote: Note = {
          ...payload,
          id: tempId,
          createdAt: now,
          updatedDate: now,
          creatorId: currentUser?.id ?? '',
        };

        const patchResult = dispatch(
          notesApi.util.updateQueryData(
            'listNotes',
            {
              entityType: payload.entityType,
              entityId: payload.entityId,
              limit: NOTES_LIST_LIMIT,
            },
            (draft) => {
              draft.data.unshift(optimisticNote);
            },
          ),
        );

        try {
          const { data } = await queryFulfilled;
          dispatch(
            notesApi.util.updateQueryData(
              'listNotes',
              {
                entityType: data.entityType,
                entityId: data.entityId,
                limit: NOTES_LIST_LIMIT,
              },
              (draft) => {
                const index = draft.data.findIndex(
                  (note) => note.id === tempId,
                );
                if (index !== -1) {
                  draft.data[index] = data;
                }
              },
            ),
          );

          tempToRealNoteId.set(tempId, data.id);
          dispatch(alertSnackbar({ successMessage: 'Note created' }));
        } catch (err) {
          patchResult.undo();

          const { message } = ensureApiError(err);
          dispatch(alertSnackbar({ errorMessage: message }));
        }
      },
    }),

    updateNote: build.mutation<Note, UpdateNoteParams>({
      query: ({ id, title, content }) => ({
        path: `/v1/notes/${resolveNoteId(id)}`,
        method: ApiMethods.patch,
        options: { body: { title, content } },
      }),
      async onQueryStarted(payload, { dispatch, queryFulfilled }) {
        const id = resolveNoteId(payload.id);
        if (id.startsWith('temp-')) return;
        const patchResult = dispatch(
          notesApi.util.updateQueryData(
            'listNotes',
            {
              entityType: payload.entityType,
              entityId: payload.entityId,
              limit: NOTES_LIST_LIMIT,
            },
            (draft) => {
              const index = draft.data.findIndex((note) => note.id === id);
              if (index !== -1) {
                draft.data[index] = {
                  ...draft.data[index],
                  ...payload,
                  id,
                  updatedDate: new Date().toISOString(),
                };
              }
            },
          ),
        );

        try {
          const { data } = await queryFulfilled;
          dispatch(
            notesApi.util.updateQueryData(
              'listNotes',
              {
                entityType: data.entityType,
                entityId: data.entityId,
                limit: NOTES_LIST_LIMIT,
              },
              (draft) => {
                const index = draft.data.findIndex(
                  (note) => note.id === data.id,
                );
                if (index !== -1) {
                  draft.data[index] = data;
                }
              },
            ),
          );
          tempToRealNoteId.delete(payload.id);
          dispatch(alertSnackbar({ successMessage: 'Note updated' }));
        } catch (err) {
          patchResult.undo();
          const { message } = ensureApiError(err);
          dispatch(alertSnackbar({ errorMessage: message }));
        }
      },
    }),
    getNote: build.query<Note, { id: string }>({
      query: ({ id }) => ({
        path: `/v1/notes/${resolveNoteId(id)}`,
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: (result) =>
        result
          ? [
              { type: ApiTags.notes, id: result.id },
              { type: ApiTags.notes, id: 'LIST' },
            ]
          : [],
    }),
    listNotes: build.query<ListNotesResponse, ListNotesParams>({
      query: (params) => {
        return {
          path: `/v1/notes?entityId=${params.entityId}&entityType=${params.entityType.toUpperCase()}&limit=${params.limit}${
            params.nextToken ? `&nextToken=${params.nextToken}` : ''
          }`,
          method: ApiMethods.get,
          options: {},
        };
      },
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const {
            data: { nextToken },
          } = await queryFulfilled;

          // keep fetching next pages while we have a nextToken
          let currentToken = nextToken;
          while (currentToken) {
            const response = await dispatch(
              notesApi.endpoints.listNotes.initiate(
                {
                  ...arg,
                  nextToken: currentToken,
                },
                { forceRefetch: true },
              ),
            ).unwrap();

            currentToken = response.nextToken;
          }
        } catch (err) {
          const { message } = ensureApiError(err);
          dispatch(alertSnackbar({ errorMessage: message }));
        }
      },
      providesTags: () => [{ type: ApiTags.notes, id: 'LIST' }],
    }),
    deleteNote: build.mutation<
      { id: string },
      { id: string; entityType: NoteEntityType; entityId: string }
    >({
      query: ({ id }) => ({
        path: `/v1/notes/${resolveNoteId(id)}`,
        method: ApiMethods.del,
        options: {},
      }),
      async onQueryStarted(variables, { dispatch, queryFulfilled }) {
        const id = resolveNoteId(variables.id);
        if (id.startsWith('temp-')) return;
        const { entityType, entityId } = variables;
        const patchResult = dispatch(
          notesApi.util.updateQueryData(
            'listNotes',
            { entityType, entityId, limit: NOTES_LIST_LIMIT },
            (draft) => {
              draft.data = draft.data.filter((note) => note.id !== id);
            },
          ),
        );
        try {
          await queryFulfilled;
          tempToRealNoteId.delete(variables.id);
          dispatch(alertSnackbar({ successMessage: 'Note deleted' }));
        } catch (err) {
          patchResult.undo();
          const { message } = ensureApiError(err);
          dispatch(alertSnackbar({ errorMessage: message }));
        }
      },
    }),
  }),
});

export const {
  useCreateNoteMutation,
  useUpdateNoteMutation,
  useGetNoteQuery,
  useListNotesQuery,
  useDeleteNoteMutation,
} = notesApi;

export type NoteQueryOptions = {
  entityType: 'CLIENT' | 'COMPANY';
  entityId: string;
};
